import React, { useState, useEffect } from 'react'
import { platformAPI, subscriptionTiersAPI } from '../services/api'
import {
  X,
  Sparkles,
  Building2,
  Mail,
  Globe,
  AlertCircle,
  Loader,
} from 'lucide-react'

const DEMO_LANGUAGES = [
  { value: 'pt', label: 'Português (pt)' },
  { value: 'en', label: 'Inglês (en)' },
  { value: 'es', label: 'Espanhol (es)' },
]

const initialForm = {
  name: '',
  slug: '',
  owner_email: '',
  billing_email: '',
  subscription_tier: 'estudio-pro',
  demo_language: 'pt',
}

function apiErrorMessage(err, fallback = 'Algo deu errado. Tente novamente.') {
  const data = err?.response?.data
  if (typeof data?.error === 'string') return data.error
  if (data?.error && typeof data.error === 'object') {
    return data.error.message || data.error.code || fallback
  }
  return data?.message || err?.message || fallback
}

const generateSlug = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50)

export const CreateDemoTenantModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState(initialForm)
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState('')
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState({})
  const [subscriptionTiers, setSubscriptionTiers] = useState([])
  const [tiersLoading, setTiersLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let canceled = false
    const loadTiers = async () => {
      setTiersLoading(true)
      try {
        const res = await subscriptionTiersAPI.getAll()
        const tiers = res?.data?.data || []
        if (canceled) return
        setSubscriptionTiers(tiers)
        const preferred =
          tiers.find((t) => t.name === 'estudio-pro') ||
          tiers.find((t) => t.analytics_enabled) ||
          tiers[0]
        if (preferred) {
          setFormData((prev) => ({ ...prev, subscription_tier: preferred.name }))
        }
      } catch (err) {
        console.error('Failed to load subscription tiers:', err)
      } finally {
        if (!canceled) setTiersLoading(false)
      }
    }
    loadTiers()
    return () => {
      canceled = true
    }
  }, [isOpen])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleNameChange = (name) => {
    const prevAuto = generateSlug(formData.name)
    handleInputChange('name', name)
    if (!formData.slug || formData.slug === prevAuto) {
      handleInputChange('slug', generateSlug(name))
    }
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.name.trim()) errors.name = 'Informe o nome do estúdio'
    if (!formData.slug.trim()) {
      errors.slug = 'Informe o slug'
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'O slug só pode conter letras minúsculas, números e hífens'
    } else if (formData.slug.length < 3) {
      errors.slug = 'O slug deve ter pelo menos 3 caracteres'
    }
    if (!formData.owner_email.trim()) {
      errors.owner_email = 'Informe o e-mail do proprietário'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
      errors.owner_email = 'Informe um endereço de e-mail válido'
    }
    if (!formData.billing_email.trim()) {
      errors.billing_email = 'Informe o e-mail de cobrança'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billing_email)) {
      errors.billing_email = 'Informe um endereço de e-mail válido'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const resetAndClose = () => {
    if (loading) return
    setFormData(initialForm)
    setError('')
    setValidationErrors({})
    setStep('')
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    setError('')

    const slug = formData.slug.trim()

    try {
      setStep('Criando estúdio...')
      const createRes = await platformAPI.createTenant({
        name: formData.name.trim(),
        slug,
        owner_email: formData.owner_email.trim(),
        billing_email: formData.billing_email.trim(),
        subscription_tier: formData.subscription_tier,
        branding: { business_name: formData.name.trim() },
      })

      const tenantData = createRes.data?.data
      const ownerCredentials = createRes.data?.owner_credentials

      if (tenantData && !tenantData.is_active) {
        setStep('Ativando estúdio...')
        await platformAPI.updateTenant(tenantData.id, {
          is_active: true,
          subscription_status: 'active',
        })
      }

      setStep('Gerando dados de demonstração...')
      const seedRes = await platformAPI.seedDemoData(slug, {
        language: formData.demo_language,
        reseed: false,
      })

      const summary = {
        tenant: tenantData,
        slug,
        ownerCredentials,
        demo: seedRes.data,
        studioUrl: `https://${slug}.vestirstudio.com`,
      }
      onSuccess?.(summary)
      setFormData(initialForm)
      setValidationErrors({})
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao criar o estúdio de demonstração.'))
    } finally {
      setLoading(false)
      setStep('')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-admin-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg mr-3">
              <Sparkles className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-admin-900">Criar estúdio de demonstração</h2>
              <p className="text-sm text-admin-600">
                Cria um estúdio e o preenche com clientes, conversas e análises fictícios.
              </p>
            </div>
          </div>
          <button
            onClick={resetAndClose}
            disabled={loading}
            aria-label="Fechar"
            className="p-2 text-admin-400 hover:text-admin-600 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-admin-900 flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                Informações do estúdio
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-admin-700 mb-1">
                    Nome do estúdio *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className={`input ${validationErrors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="Estúdio Demonstração"
                    maxLength={255}
                    disabled={loading}
                  />
                  {validationErrors.name && (
                    <p className="text-red-600 text-xs mt-1">{validationErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-admin-700 mb-1">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    className={`input font-mono text-sm ${validationErrors.slug ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    placeholder="estudio-demo"
                    maxLength={50}
                    disabled={loading}
                  />
                  {validationErrors.slug && (
                    <p className="text-red-600 text-xs mt-1">{validationErrors.slug}</p>
                  )}
                  <p className="text-xs text-admin-500 mt-1">
                    Usado no subdomínio: <code>{formData.slug || 'slug'}.vestirstudio.com</code>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-admin-700 mb-1">
                    E-mail do proprietário *
                  </label>
                  <div className="relative">
                    <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                    <input
                      type="email"
                      value={formData.owner_email}
                      onChange={(e) => handleInputChange('owner_email', e.target.value)}
                      className={`input pl-10 ${validationErrors.owner_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      placeholder="proprietario@estudio-demo.com.br"
                      disabled={loading}
                    />
                  </div>
                  {validationErrors.owner_email && (
                    <p className="text-red-600 text-xs mt-1">{validationErrors.owner_email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-admin-700 mb-1">
                    E-mail de cobrança *
                  </label>
                  <div className="relative">
                    <Mail className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                    <input
                      type="email"
                      value={formData.billing_email}
                      onChange={(e) => handleInputChange('billing_email', e.target.value)}
                      className={`input pl-10 ${validationErrors.billing_email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      placeholder="financeiro@estudio-demo.com.br"
                      disabled={loading}
                    />
                  </div>
                  {validationErrors.billing_email && (
                    <p className="text-red-600 text-xs mt-1">{validationErrors.billing_email}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Plano de assinatura *
                </label>
                {tiersLoading ? (
                  <div className="flex items-center gap-2 text-sm text-admin-500 py-3">
                    <Loader className="h-4 w-4 animate-spin" />
                    Carregando planos...
                  </div>
                ) : subscriptionTiers.length === 0 ? (
                  <p className="text-sm text-admin-500 py-3">Nenhum plano de assinatura disponível.</p>
                ) : (
                  <select
                    value={formData.subscription_tier}
                    onChange={(e) => handleInputChange('subscription_tier', e.target.value)}
                    className="input"
                    disabled={loading}
                  >
                    {subscriptionTiers.map((tier) => (
                      <option key={tier.name} value={tier.name}>
                        {tier.display_name || tier.name}
                        {tier.analytics_enabled ? ' · Análises' : ''}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-admin-500 mt-1">
                  Recomendado: um plano com análises habilitadas para que os gráficos da demonstração sejam exibidos corretamente.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-admin-700 mb-1">
                  Idioma da demonstração *
                </label>
                <div className="relative">
                  <Globe className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-admin-400" />
                  <select
                    value={formData.demo_language}
                    onChange={(e) => handleInputChange('demo_language', e.target.value)}
                    className="input pl-10"
                    disabled={loading}
                  >
                    {DEMO_LANGUAGES.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-admin-500 mt-1">
                  Define os nomes, respostas e roteiros de WhatsApp usados ao gerar os dados fictícios.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-admin-200">
              <button
                type="button"
                onClick={resetAndClose}
                disabled={loading}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? (
                  <div className="flex items-center">
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    {step || 'Processando...'}
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Criar estúdio de demonstração
                  </div>
                )}
              </button>
            </div>
        </form>
      </div>
    </div>
  )
}
